import { AlertTriangle, PauseCircle, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';

import { useUpdateMessageThreadMutation } from '@/modules/messaging/mutations/useUpdateMessageThreadMutation';
import type { MessageWithThreadApi } from '@/modules/messaging/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface ThreadHeaderProps {
  // The base thread shape (not the conversation list's `MessageThreadApi`),
  // so this header also works for `DraftMessages`, whose thread has no
  // `summary` field.
  thread: MessageWithThreadApi['thread'];
}

export const ThreadHeader = ({ thread }: ThreadHeaderProps) => {
  const title =
    `${thread.candidate.firstName || ''} ${thread.candidate.lastName || ''}`.trim() ||
    'Unknown';

  const isRequiresReview = thread.status === 'ReviewRequired';
  const isPaused = thread.status === 'Paused';
  const isActive = thread.status === 'Active';

  const updateThreadMutation = useUpdateMessageThreadMutation();

  const handleTogglePause = () => {
    const newStatus = isActive ? 'Paused' : 'Active';
    updateThreadMutation.mutate(
      {
        threadId: thread.id,
        status: newStatus,
      },
      {
        onSuccess: () => {
          toast.success(`Conversation ${isActive ? 'paused' : 'resumed'}`);
        },
        onError: (error) => {
          toast.error(
            `Failed to ${isActive ? 'pause' : 'resume'} conversation: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  return (
    <div className="border-border bg-background flex items-center border-b p-4">
      <span className="font-semibold">{title}</span>
      {thread.candidate.email && (
        <span className="text-muted-foreground ml-2">
          ({thread.candidate.email})
        </span>
      )}

      <div className="ml-auto flex items-center gap-2">
        {isRequiresReview && (
          <Badge
            variant="outline"
            className="border-semantic-540 text-semantic-540 bg-semantic-540/10"
          >
            <AlertTriangle size={14} />
            Review Required
          </Badge>
        )}

        {isPaused && (
          <Badge
            variant="outline"
            className="border-semantic-540 text-semantic-540 bg-semantic-540/10"
          >
            <PauseCircle size={14} />
            Paused
          </Badge>
        )}

        <Button
          variant={isActive ? 'outline' : 'primary'}
          size="sm"
          onClick={handleTogglePause}
          disabled={updateThreadMutation.isPending}
          className="gap-1.5"
        >
          {isActive ? (
            <>
              <PauseCircle size={14} />
              Pause
            </>
          ) : (
            <>
              <PlayCircle size={14} />
              Resume
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
